import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Building2, Users, DollarSign, Calendar, Search, Edit, Trash2, 
  Clock, AlertTriangle, CheckCircle, XCircle, Plus, Settings,
  CreditCard, TrendingUp, RefreshCw, Mail, MailCheck, MailX, Key, LogIn, Globe
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PlatformAccountsPage({ user, onLogout }) {
  const [companies, setCompanies] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialogs
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExtendTrialDialog, setShowExtendTrialDialog] = useState(false);
  const [showAddPaymentDialog, setShowAddPaymentDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  
  // Selected company for actions
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [extendDays, setExtendDays] = useState(14);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    monthly_subscription_amount: 4999,
    yearly_subscription_amount: 53989,
    business_monthly_amount: 7999,
    business_yearly_amount: 86389,
    trial_period_days: 14,
    square_application_id: '',
    square_access_token: '',
    square_location_id: '',
    square_environment: 'sandbox',
    square_webhook_signature_key: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [companiesRes, paymentsRes, statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/platform/companies`),
        axios.get(`${API}/platform/subscription-payments`),
        axios.get(`${API}/platform/stats`),
        axios.get(`${API}/platform/settings`)
      ]);
      setCompanies(companiesRes.data);
      setPayments(paymentsRes.data);
      setStats(statsRes.data);
      setPlatformSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (error) {
      console.error('Failed to load platform data:', error);
      toast.error('Failed to load platform data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setEditForm({
      company_name: company.company_name,
      contact_name: company.contact_name,
      email: company.email,
      phone: company.phone || '',
      account_status: company.account_status || 'trial',
      plan_tier: company.plan_tier || 'free',
      is_active: company.is_active !== false,
      next_payment_due: company.next_payment_due,
      last_payment_date: company.last_payment_date,
      trial_end: company.trial_end,
      email_verified: company.email_verified
    });
    setShowEditDialog(true);
  };

  const saveCompanyEdit = async () => {
    try {
      await axios.put(`${API}/platform/companies/${selectedCompany.id}`, editForm);
      toast.success('Company updated successfully');
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update company');
    }
  };

  const handleExtendTrial = (company) => {
    setSelectedCompany(company);
    setExtendDays(14);
    setShowExtendTrialDialog(true);
  };

  const saveExtendTrial = async () => {
    try {
      await axios.post(`${API}/platform/companies/${selectedCompany.id}/extend-trial?days=${extendDays}`);
      toast.success(`Trial extended by ${extendDays} days`);
      setShowExtendTrialDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to extend trial');
    }
  };

  const handleDeleteCompany = (company) => {
    setSelectedCompany(company);
    setDeleteConfirmName('');
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (deleteConfirmName !== selectedCompany.company_name) {
      toast.error('Company name does not match');
      return;
    }
    try {
      await axios.delete(`${API}/platform/companies/${selectedCompany.id}?confirm=true`);
      toast.success('Company and all data deleted');
      setShowDeleteDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(`Are you sure you want to delete this payment of ${formatCurrency(payment.amount)} for ${payment.company_name}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/platform/subscription-payments/${payment.id}`);
      toast.success('Payment deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const handleAddPayment = (company) => {
    setSelectedCompany(company);
    setPaymentAmount((platformSettings?.monthly_subscription_amount / 100).toFixed(2));
    setPaymentNotes('');
    setShowAddPaymentDialog(true);
  };

  const savePayment = async () => {
    const amountCents = Math.round(parseFloat(paymentAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await axios.post(`${API}/platform/subscription-payments?company_id=${selectedCompany.id}&amount=${amountCents}&notes=${encodeURIComponent(paymentNotes)}`);
      toast.success('Payment recorded');
      setShowAddPaymentDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/platform/settings`, settingsForm);
      toast.success('Settings saved');
      setShowSettingsDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleVerifyEmail = async (company) => {
    try {
      await axios.post(`${API}/platform/companies/${company.id}/verify-email`);
      toast.success(`Email verified for ${company.email}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify email');
    }
  };

  const handleChangePassword = (company) => {
    setSelectedCompany(company);
    setNewPassword('');
    setConfirmPassword('');
    setShowChangePasswordDialog(true);
  };

  const saveChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await axios.post(`${API}/platform/companies/${selectedCompany.id}/change-password`, {
        new_password: newPassword
      });
      toast.success(`Password changed for ${selectedCompany.email}`);
      setShowChangePasswordDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleLoginAs = async (company) => {
    try {
      const response = await axios.post(`${API}/platform/companies/${company.id}/impersonate`);
      const { token, user } = response.data;
      
      // Open in a new tab with the impersonated session
      // Store the token and user info in a URL-safe way
      const impersonateUrl = `${window.location.origin}/dashboard?impersonate=${encodeURIComponent(token)}&user=${encodeURIComponent(JSON.stringify(user))}`;
      
      // Open new window
      const newWindow = window.open(impersonateUrl, '_blank');
      
      if (newWindow) {
        toast.success(`Opening session as ${company.company_name}`);
      } else {
        // If popup was blocked, provide alternative
        toast.error('Popup blocked. Please allow popups for this site.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as user');
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = 
      company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.contact_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || company.account_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      trial: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
      cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs border ${styles[status] || styles.trial}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Trial'}
      </span>
    );
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-obsidian">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neon-blue"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-obsidian">
      <Sidebar user={user} onLogout={onLogout} />
      <div className="flex-1 overflow-auto">
        <div className="p-8 space-y-6" data-testid="platform-accounts-page">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold">Platform Accounts</h1>
              <p className="text-muted-foreground">Manage DJ companies, billing, and subscriptions</p>
            </div>
            <Button onClick={() => setShowSettingsDialog(true)} variant="outline" className="border-border">
              <Settings className="w-4 h-4 mr-2" />
              Billing Settings
            </Button>
          </div>

          {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-neon-blue" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_companies || 0}</p>
                <p className="text-sm text-muted-foreground">Total Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold">{stats?.active_trials || 0}</p>
                <p className="text-sm text-muted-foreground">Active Trials</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold">{stats?.active_subscriptions || 0}</p>
                <p className="text-sm text-muted-foreground">Active Subscriptions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-2xl font-bold">{stats?.suspended_accounts || 0}</p>
                <p className="text-sm text-muted-foreground">Suspended</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-neon-purple" />
              <div>
                <p className="text-2xl font-bold">{stats?.total_revenue_formatted || '$0.00'}</p>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Globe className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-2xl font-bold">{companies.filter(c => c.plan_tier === 'business').length}</p>
                <p className="text-sm text-muted-foreground">Business Plan</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="companies" className="w-full">
        <TabsList className="bg-muted/30 border border-border">
          <TabsTrigger value="companies" className="data-[state=active]:bg-neon-blue/20 data-[state=active]:text-neon-blue">
            <Building2 className="w-4 h-4 mr-2" />
            DJ Companies
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-neon-blue/20 data-[state=active]:text-neon-blue">
            <CreditCard className="w-4 h-4 mr-2" />
            Payment History
          </TabsTrigger>
        </TabsList>

        {/* Companies Tab */}
        <TabsContent value="companies" className="mt-4">
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-muted/20 border-border"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-muted/20 border-border">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData} className="border-border">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {/* Companies List */}
          <div className="space-y-3">
            {filteredCompanies.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No companies found</p>
                </CardContent>
              </Card>
            ) : (
              filteredCompanies.map((company) => (
                <Card key={company.id} className="bg-card border-border hover:border-neon-blue/30 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{company.company_name}</h3>
                          {getStatusBadge(company.account_status)}
                          {company.plan_tier === 'business' && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30" data-testid={`plan-badge-${company.id}`}>
                              Business
                            </span>
                          )}
                          {company.plan_tier === 'pro' && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" data-testid={`plan-badge-${company.id}`}>
                              Pro
                            </span>
                          )}
                          {company.is_platform_admin && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-neon-purple/20 text-neon-purple border border-neon-purple/30">
                              Platform Admin
                            </span>
                          )}
                          {/* Email Verification Badge */}
                          {company.email_verified === false && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30 flex items-center gap-1">
                              <MailX className="w-3 h-3" />
                              Email Not Verified
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Contact</p>
                            <p>{company.contact_name}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Email</p>
                            <p className="flex items-center gap-1">
                              {company.email}
                              {company.email_verified !== false && (
                                <MailCheck className="w-3.5 h-3.5 text-green-400" title="Email Verified" />
                              )}
                            </p>
                          </div>
                          {/* Only show Trial Ends for trial accounts */}
                          {(!company.account_status || company.account_status === 'trial') ? (
                            <div>
                              <p className="text-muted-foreground">Trial Ends</p>
                              <p className={company.trial_days_remaining <= 3 ? 'text-red-400' : ''}>
                                {formatDate(company.trial_end)}
                                {company.trial_days_remaining !== undefined && (
                                  <span className="text-muted-foreground ml-1">
                                    ({company.trial_days_remaining} days left)
                                  </span>
                                )}
                              </p>
                            </div>
                          ) : (
                            <div>
                              <p className="text-muted-foreground">Status Since</p>
                              <p className="capitalize">{company.account_status}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-muted-foreground">Usage</p>
                            <p>{company.booking_count || 0} bookings, {company.lead_count || 0} leads</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        {/* Manual Verify Email Button - only show if not verified */}
                        {company.email_verified === false && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVerifyEmail(company)}
                            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                            data-testid={`verify-email-${company.id}`}
                            title="Manually verify email"
                          >
                            <MailCheck className="w-4 h-4 mr-1" />
                            Verify
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleLoginAs(company)}
                          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                          data-testid={`login-as-${company.id}`}
                          title="Login as this user"
                        >
                          <LogIn className="w-4 h-4 mr-1" />
                          Login As
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChangePassword(company)}
                          className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                          data-testid={`change-password-${company.id}`}
                          title="Change password"
                        >
                          <Key className="w-4 h-4 mr-1" />
                          Password
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddPayment(company)}
                          className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                          data-testid={`add-payment-${company.id}`}
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Payment
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleExtendTrial(company)}
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                          data-testid={`extend-trial-${company.id}`}
                        >
                          <Clock className="w-4 h-4 mr-1" />
                          Extend
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditCompany(company)}
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`edit-company-${company.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {!company.is_platform_admin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteCompany(company)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="mt-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-neon-blue" />
                Subscription Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No payments recorded yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 bg-muted/20 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-2 h-2 rounded-full ${
                          payment.status === 'completed' ? 'bg-green-500' :
                          payment.status === 'pending' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`} />
                        <div>
                          <p className="font-medium">{payment.company_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {payment.payment_method === 'manual' ? 'Manual Payment' : 'Square'} 
                            {payment.notes && ` - ${payment.notes}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold text-green-400">{formatCurrency(payment.amount)}</p>
                          <p className="text-sm text-muted-foreground">{formatDate(payment.created_at)}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePayment(payment)}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`delete-payment-btn-${payment.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Company Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={editForm.company_name || ''}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                className="bg-muted/20 border-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Name</Label>
              <Input
                value={editForm.contact_name || ''}
                onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                className="bg-muted/20 border-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="bg-muted/20 border-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="bg-muted/20 border-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Account Status</Label>
              <Select
                value={editForm.account_status || 'trial'}
                onValueChange={(v) => setEditForm({ ...editForm, account_status: v })}
              >
                <SelectTrigger className="bg-muted/20 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Plan Tier</Label>
              <Select
                value={editForm.plan_tier || 'free'}
                onValueChange={(v) => setEditForm({ ...editForm, plan_tier: v })}
              >
                <SelectTrigger className="bg-muted/20 border-border" data-testid="plan-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="business">Business (Website Hosting)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Business plan enables Website Hosting for this company</p>
            </div>
            
            {/* Billing Information - Read Only */}
            <div className="border-t border-border pt-4 mt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4" />
                Account Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {/* Email Verification Status */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Email Status</Label>
                  <div className="flex items-center gap-2">
                    {editForm.email_verified === false ? (
                      <>
                        <span className="text-orange-400 font-medium flex items-center gap-1">
                          <MailX className="w-4 h-4" />
                          Not Verified
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            handleVerifyEmail(selectedCompany);
                            setEditForm({ ...editForm, email_verified: true });
                          }}
                          className="h-6 px-2 text-xs border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        >
                          Verify Now
                        </Button>
                      </>
                    ) : (
                      <span className="text-green-400 font-medium flex items-center gap-1">
                        <MailCheck className="w-4 h-4" />
                        Verified
                      </span>
                    )}
                  </div>
                </div>
                {/* Trial Ends - only show for trial accounts */}
                {(!editForm.account_status || editForm.account_status === 'trial') && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Trial Ends</Label>
                    <p className="font-medium">
                      {editForm.trial_end ? new Date(editForm.trial_end).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Last Payment</Label>
                  <p className="font-medium">
                    {editForm.last_payment_date ? new Date(editForm.last_payment_date).toLocaleDateString() : 'Never'}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Next Billing Date</Label>
                  <p className={`font-medium ${editForm.next_payment_due && new Date(editForm.next_payment_due) < new Date() ? 'text-red-500' : 'text-green-500'}`}>
                    {editForm.next_payment_due ? (
                      <>
                        {new Date(editForm.next_payment_due).toLocaleDateString()}
                        {new Date(editForm.next_payment_due) < new Date() && (
                          <span className="text-red-500 text-xs ml-2">(Overdue)</span>
                        )}
                      </>
                    ) : 'Not set'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={saveCompanyEdit} className="bg-neon-blue text-black hover:bg-neon-blue/80">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Trial Dialog */}
      <Dialog open={showExtendTrialDialog} onOpenChange={setShowExtendTrialDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Extend Trial Period</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground mb-4">
              Extend trial for <span className="text-foreground font-medium">{selectedCompany?.company_name}</span>
            </p>
            <div className="space-y-2">
              <Label>Days to Add</Label>
              <Input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                min="1"
                max="365"
                className="bg-muted/20 border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtendTrialDialog(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={saveExtendTrial} className="bg-blue-500 text-white hover:bg-blue-600">
              Extend Trial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Company Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Company - DANGER
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
              <p className="text-red-400 font-medium">This action cannot be undone!</p>
              <p className="text-sm text-muted-foreground mt-2">
                This will permanently delete <span className="text-foreground font-medium">{selectedCompany?.company_name}</span> and ALL associated data including:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 ml-4 list-disc">
                <li>All bookings and leads</li>
                <li>All clients and contacts</li>
                <li>All contracts and invoices</li>
                <li>All employees and settings</li>
                <li>All forms, templates, and documents</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label>Type the company name to confirm:</Label>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={selectedCompany?.company_name}
                className="bg-muted/20 border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-border">
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteConfirmName !== selectedCompany?.company_name}
              className="bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
            >
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-purple-400" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground mb-4">
              Set a new password for <span className="text-foreground font-medium">{selectedCompany?.company_name}</span>
              <br />
              <span className="text-sm">({selectedCompany?.email})</span>
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-muted/20 border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="bg-muted/20 border-border"
                />
              </div>
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-400 text-sm">Passwords do not match</p>
              )}
              {newPassword && newPassword.length < 6 && (
                <p className="text-orange-400 text-sm">Password must be at least 6 characters</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePasswordDialog(false)} className="border-border">
              Cancel
            </Button>
            <Button 
              onClick={saveChangePassword} 
              className="bg-purple-500 text-white hover:bg-purple-600"
              disabled={!newPassword || newPassword.length < 6 || newPassword !== confirmPassword}
            >
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog open={showAddPaymentDialog} onOpenChange={setShowAddPaymentDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground mb-4">
              Record a payment for <span className="text-foreground font-medium">{selectedCompany?.company_name}</span>
            </p>
            
            {/* Quick Pay with Square buttons */}
            {platformSettings?.square_access_token && platformSettings?.square_location_id && (
              <div className="mb-6 p-4 rounded-lg bg-muted/20 border border-border">
                <p className="text-sm font-medium mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Pay with Square
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await axios.post(
                          `${API}/platform/create-subscription-checkout?company_id=${selectedCompany.id}&billing_type=monthly`
                        );
                        if (response.data.checkout_url) {
                          window.open(response.data.checkout_url, '_blank');
                          toast.success('Square checkout opened. Payment will be auto-recorded via webhook.');
                          setShowAddPaymentDialog(false);
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to create checkout');
                      }
                    }}
                    className="bg-purple-500 text-white hover:bg-purple-600"
                  >
                    Monthly (${(platformSettings?.monthly_subscription_amount / 100).toFixed(2)})
                  </Button>
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await axios.post(
                          `${API}/platform/create-subscription-checkout?company_id=${selectedCompany.id}&billing_type=yearly`
                        );
                        if (response.data.checkout_url) {
                          window.open(response.data.checkout_url, '_blank');
                          toast.success('Square checkout opened. Payment will be auto-recorded via webhook.');
                          setShowAddPaymentDialog(false);
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to create checkout');
                      }
                    }}
                    className="bg-green-500 text-white hover:bg-green-600"
                  >
                    Yearly (${(platformSettings?.yearly_subscription_amount / 100).toFixed(2)})
                    <span className="ml-1 text-xs opacity-80">10% off</span>
                  </Button>
                </div>
                {selectedCompany?.plan_tier === 'business' && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await axios.post(
                            `${API}/platform/create-subscription-checkout?company_id=${selectedCompany.id}&billing_type=business_monthly`
                          );
                          if (response.data.checkout_url) {
                            window.open(response.data.checkout_url, '_blank');
                            toast.success('Square checkout opened.');
                            setShowAddPaymentDialog(false);
                          }
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to create checkout');
                        }
                      }}
                      className="bg-amber-500 text-black hover:bg-amber-600"
                      data-testid="square-business-monthly-btn"
                    >
                      Business Mo. (${(platformSettings?.business_monthly_amount / 100).toFixed(2)})
                    </Button>
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await axios.post(
                            `${API}/platform/create-subscription-checkout?company_id=${selectedCompany.id}&billing_type=business_yearly`
                          );
                          if (response.data.checkout_url) {
                            window.open(response.data.checkout_url, '_blank');
                            toast.success('Square checkout opened.');
                            setShowAddPaymentDialog(false);
                          }
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed to create checkout');
                        }
                      }}
                      className="bg-amber-600 text-white hover:bg-amber-700"
                      data-testid="square-business-yearly-btn"
                    >
                      Business Yr. (${(platformSettings?.business_yearly_amount / 100).toFixed(2)})
                      <span className="ml-1 text-xs opacity-80">10% off</span>
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {platformSettings?.square_access_token ? 'Or record a manual payment:' : 'Record a payment:'}
              </p>
              
              {/* Quick amount buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setPaymentAmount((platformSettings?.monthly_subscription_amount / 100).toFixed(2));
                    setPaymentNotes('Monthly subscription');
                  }}
                  className="border-border"
                >
                  Monthly (${(platformSettings?.monthly_subscription_amount / 100 || 49.99).toFixed(2)})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setPaymentAmount((platformSettings?.yearly_subscription_amount / 100).toFixed(2));
                    setPaymentNotes('Yearly subscription (10% off)');
                  }}
                  className="border-border text-green-500 border-green-500/50"
                >
                  Yearly (${(platformSettings?.yearly_subscription_amount / 100 || 539.89).toFixed(2)})
                  <span className="ml-1 text-xs">10% off</span>
                </Button>
                {selectedCompany?.plan_tier === 'business' && (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPaymentAmount((platformSettings?.business_monthly_amount / 100).toFixed(2));
                        setPaymentNotes('Business plan - Monthly');
                      }}
                      className="border-border text-amber-400 border-amber-500/50"
                      data-testid="manual-business-monthly-btn"
                    >
                      Biz Monthly (${(platformSettings?.business_monthly_amount / 100 || 79.99).toFixed(2)})
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPaymentAmount((platformSettings?.business_yearly_amount / 100).toFixed(2));
                        setPaymentNotes('Business plan - Yearly (10% off)');
                      }}
                      className="border-border text-amber-400 border-amber-500/50"
                      data-testid="manual-business-yearly-btn"
                    >
                      Biz Yearly (${(platformSettings?.business_yearly_amount / 100 || 863.89).toFixed(2)})
                      <span className="ml-1 text-xs">10% off</span>
                    </Button>
                  </>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="49.99"
                  className="bg-muted/20 border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g., Monthly subscription - March 2026"
                  className="bg-muted/20 border-border"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPaymentDialog(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={savePayment} className="bg-green-500 text-white hover:bg-green-600">
              Record Manual Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Billing Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Trial Period (days)</Label>
              <Input
                type="number"
                min="1"
                max="365"
                value={settingsForm.trial_period_days}
                onChange={(e) => setSettingsForm({ ...settingsForm, trial_period_days: parseInt(e.target.value) || 14 })}
                className="bg-muted/20 border-border"
                data-testid="trial-period-input"
              />
              <p className="text-xs text-muted-foreground">Number of days for new account trials. Changes only affect new signups.</p>
            </div>

            <div className="border-t border-border pt-4">
              <h4 className="font-medium mb-3">Standard Plan Pricing</h4>
            </div>
            <div className="space-y-2">
              <Label>Monthly Subscription Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={(settingsForm.monthly_subscription_amount / 100).toFixed(2)}
                onChange={(e) => {
                  const monthly = Math.round(parseFloat(e.target.value) * 100) || 0;
                  const yearly = Math.round(monthly * 12 * 0.9); // 10% discount
                  setSettingsForm({ 
                    ...settingsForm, 
                    monthly_subscription_amount: monthly,
                    yearly_subscription_amount: yearly
                  });
                }}
                className="bg-muted/20 border-border"
              />
              <p className="text-xs text-muted-foreground">Monthly billing amount for DJ company subscriptions</p>
            </div>
            
            <div className="space-y-2">
              <Label>Yearly Subscription Amount ($) <span className="text-green-500 text-xs ml-1">10% off</span></Label>
              <Input
                type="number"
                step="0.01"
                value={(settingsForm.yearly_subscription_amount / 100).toFixed(2)}
                onChange={(e) => setSettingsForm({ 
                  ...settingsForm, 
                  yearly_subscription_amount: Math.round(parseFloat(e.target.value) * 100) || 0 
                })}
                className="bg-muted/20 border-border"
              />
              <p className="text-xs text-muted-foreground">
                Annual billing amount (auto-calculated as monthly × 12 × 0.9, but can be customized)
              </p>
              <p className="text-xs text-green-500">
                Savings: ${((settingsForm.monthly_subscription_amount * 12 - settingsForm.yearly_subscription_amount) / 100).toFixed(2)}/year
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-amber-400" />
                Business Plan (Website Hosting)
              </h4>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Business Monthly Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={(settingsForm.business_monthly_amount / 100).toFixed(2)}
                    onChange={(e) => {
                      const monthly = Math.round(parseFloat(e.target.value) * 100) || 0;
                      const yearly = Math.round(monthly * 12 * 0.9);
                      setSettingsForm({ 
                        ...settingsForm, 
                        business_monthly_amount: monthly,
                        business_yearly_amount: yearly
                      });
                    }}
                    className="bg-muted/20 border-border"
                    data-testid="business-monthly-input"
                  />
                  <p className="text-xs text-muted-foreground">Monthly billing for Business tier (includes website hosting)</p>
                </div>
                <div className="space-y-2">
                  <Label>Business Yearly Amount ($) <span className="text-green-500 text-xs ml-1">10% off</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={(settingsForm.business_yearly_amount / 100).toFixed(2)}
                    onChange={(e) => setSettingsForm({ 
                      ...settingsForm, 
                      business_yearly_amount: Math.round(parseFloat(e.target.value) * 100) || 0 
                    })}
                    className="bg-muted/20 border-border"
                    data-testid="business-yearly-input"
                  />
                  <p className="text-xs text-green-500">
                    Savings: ${((settingsForm.business_monthly_amount * 12 - settingsForm.business_yearly_amount) / 100).toFixed(2)}/year
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-border pt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Square Integration
              </h4>
              
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Application ID</Label>
                  <Input
                    value={settingsForm.square_application_id || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, square_application_id: e.target.value })}
                    placeholder="sq0idp-..."
                    className="bg-muted/20 border-border font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Access Token</Label>
                  <Input
                    type="password"
                    value={settingsForm.square_access_token || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, square_access_token: e.target.value })}
                    placeholder="EAAAl..."
                    className="bg-muted/20 border-border font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location ID</Label>
                  <Input
                    value={settingsForm.square_location_id || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, square_location_id: e.target.value })}
                    placeholder="L..."
                    className="bg-muted/20 border-border font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Environment</Label>
                  <Select
                    value={settingsForm.square_environment || 'sandbox'}
                    onValueChange={(v) => setSettingsForm({ ...settingsForm, square_environment: v })}
                  >
                    <SelectTrigger className="bg-muted/20 border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Webhook Signature Key</Label>
                  <Input
                    type="password"
                    value={settingsForm.square_webhook_signature_key || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, square_webhook_signature_key: e.target.value })}
                    placeholder="Enter key from Square Developer Console"
                    className="bg-muted/20 border-border font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Get this from Square Developer Console → Your App → Webhooks
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)} className="border-border">
              Cancel
            </Button>
            <Button onClick={saveSettings} className="bg-neon-blue text-black hover:bg-neon-blue/80">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </div>
      </div>
    </div>
  );
}
